import {DOCUMENT} from '@angular/common';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Component, Inject, Injector, OnInit, Renderer2, Type} from '@angular/core';
import {FormControl, FormGroup} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';
import {
  AddonsConfig,
  AvailableAddons,
  Cart,
  Document,
  EntityParsed,
  Frequency,
  FunnelInfo, Offer,
  SelectedAddons,
  SmartTopUpPromo,
  StaticEntityKey,
  StepData,
  TotalPrice,
  TotalPriceEnum
} from 'models';
import {combineLatest, Observable, of} from 'rxjs';
import {catchError, debounceTime, filter, first, map, shareReplay, tap} from 'rxjs/operators';
import {EnvironmentService, PaypalService} from 'services';
import {ModalButtonPositionEnum, ModalType} from '../../../../../../app-shared/models/modal.interface';
import {AddonsOrderConfig, OfferAddons} from '../../../../../../app-shared/models/steps/step-offer-extras.interface';
import {DataService} from '../../../../../../app-shared/services/data/data.service';
import {ProcessStepBaseComponent} from '../../_core/process-step-base.component';

@Component({
  selector: 'sb-step-offer-extras',
  templateUrl: './offer-extras.component.html',
  styleUrls: ['./offer-extras.component.scss']
})
export class OfferExtrasComponent extends ProcessStepBaseComponent implements OnInit {
  public mgmCodeActivationMethod = 'NEW';
  public mgmCode: string;
  public couponCode: string;
  public funnelHasCoupon: boolean;
  public textNote$: Observable<string>;
  public errorMessage: string;
  public translationKey: string;
  public isInviteCodeEnabled: boolean;
  public isPaidAddonEnabled: boolean;
  public isCouponEnabled: boolean;
  public isSmartTopUpMandatory: boolean;
  public isSmartTopUpEnabled: boolean;
  public hasDoublePO: boolean;
  public readonly OFFER_EXTRAS = 'offerExtras';
  public readonly SMART_TOP_UP = 'smartTopUp';
  public readonly SELECTED_ADDONS = 'selectedAddons';
  public readonly ADDONS = 'addons';
  protected paypalService: PaypalService;
  availableAddons: OfferAddons[] = [];
  private orderConfig$: Observable<AddonsOrderConfig>;
  addonsFormGroup: FormGroup = new FormGroup({});
  shouldShowSnackbar: boolean;
  public selectedAddons: Set<string>;
  public smartTopUpPromo: SmartTopUpPromo;
  public cart: Cart;
  public offer: Offer;
  public offerAlternative: Offer;
  public deltaGiga: number;
  public incompatibilities: { [p: string]: string[] };
  public incompatibilities$: Observable<{ [p: string]: string[] }>;
  private readonly ENABLE_ADDON_ORDERING = false;

  constructor(injector: Injector,
              private renderer: Renderer2,
              @Inject(DOCUMENT) private document: Document,
              private dataService: DataService,
              private http: HttpClient) {
    super(injector);
    this.paypalService = this.injectServiceInstance(PaypalService as Type<PaypalService>);
    this.translateService = this.injectServiceInstance(TranslateService as Type<TranslateService>);
  }

  ngOnInit() {
    super.isStickyNavigation();
    super.setFormGroup(this.formsService.generateStepOfferExtrasForm());
    super.setFormStoreDataFunction(this.appStoreFacadeService.step.storeStepOfferExtrasValue);
    this.initIncompatibilities();
    this.getFormDataAndValidation();
    this.manageMGMCode();
    this.manageCoupon();
    this.getCart();
    // this.getEntitySelectedAddons();
    this.textNote$ = this.appStoreFacadeService.route.queryParams$
      .pipe(filter(Boolean), map((queryParams: any) => queryParams.textNote));
    this.renderer.addClass(document.body, 'tua-offerta');
    this.shouldShowSnackbar = false;
  }

  public checkValue(check: any) {
    this.analyticsService.sendCustomEvent({
      custom_category: 'ecommerce',
      custom_action: 'ricarica automatica',
      custom_label: check.value ? 'attiva' : 'disattiva'
    });
    this.dataService.changeValue(check.value);
  }

  private getFormDataAndValidation() {
    this.subscriptions.add(
      combineLatest([
        this.appStoreFacadeService.entity.offer$,
        this.appStoreFacadeService.entity.offerAlternative$,
        this.appStoreFacadeService.entity.funnelInfo$,
        this.appStoreFacadeService.entity.offerExtras$,
        this.appStoreFacadeService.entity.availableAddons$,
        this.appStoreFacadeService.entity.selectedAddons$,
        this.appStoreFacadeService.entity.smartTopUpPromo$,
        this.appStoreFacadeService.entity.addonsConfig$
      ])
        .pipe(
          filter((args) => args.every(Boolean))
        )
        .subscribe(([offer, offerAlternative, funnelInfo, offerExtras, availableAddons, selectedAddons, smartTopUpPromo,
                      addonsIncompatibilities]: [Offer, Offer, FunnelInfo, StepData, AvailableAddons, EntityParsed, SmartTopUpPromo,
          EntityParsed]) => {
          this.isSmartTopUpMandatory = funnelInfo.isSmartTopUpMandatory;
          this.isSmartTopUpEnabled = funnelInfo.isSmartTopUpEnabled;
          this.hasDoublePO = funnelInfo.isStuOfferDoubleConfigEnabled;
          super.setFormGroupStepData(offerExtras);
          // force extras.smartTopUp value to true if funnelInfo.isSmartTopUpMandatory
          // @ts-ignore
          const smartTopUpValue = offerExtras.value.smartTopUp;
          const addonsValue = selectedAddons.value.addons;
          this.formGroup.get(this.OFFER_EXTRAS).get(this.SMART_TOP_UP).patchValue(!!(funnelInfo.isSmartTopUpMandatory || smartTopUpValue));
          this.formGroup.get(this.SELECTED_ADDONS).get(this.ADDONS).patchValue(addonsValue || []);
          this.offer = offer;
          this.offerAlternative = offerAlternative;
          this.funnelHasCoupon = funnelInfo.hasCoupon ? funnelInfo.hasCoupon : false;
          this.isInviteCodeEnabled = funnelInfo.isInviteCodeEnabled ? funnelInfo.isInviteCodeEnabled : false;
          this.isCouponEnabled = funnelInfo.isCouponEnabled ? funnelInfo.isCouponEnabled : false;
          this.isPaidAddonEnabled = funnelInfo.isPaidAddonEnabled ? funnelInfo.isPaidAddonEnabled : false;
          this.smartTopUpPromo = smartTopUpPromo;
          this.incompatibilities = (addonsIncompatibilities && addonsIncompatibilities as unknown as AddonsConfig).incompatibilities;
          // this.availableAddons = availableAddons.addons;
          this.loadOrderConfig(availableAddons.addons).subscribe(
              orderedAddons => {
                this.availableAddons = orderedAddons;
              }
          );
          if (this.isPaidAddonEnabled && this.availableAddons) {
            this.initSelectedAddons(selectedAddons);
            this.initAddonsFormGroup();
          }
          this.deltaGiga = this.getDeltaGiga();
          setTimeout(() => {
            this.shouldShowSnackbar = true;
          }, 100);
        })
    );
    // this.subscriptions.add(
    //   this.formGroup.get('selectedAddons.addons').valueChanges
    //     .pipe(
    //       withLatestFrom(this.appStoreFacadeService.cart.cart$)
    //     ).subscribe(cart => {
    //       this.manageAddonsChange(cart);
    //   })
    // );
    this.subscriptions.add(
      this.appStoreFacadeService.step.extrasValue$
        .pipe(debounceTime(150), first(Boolean))
        .subscribe(() => {
          this.sendAnalyticsData();
        })
    );
    this.subscriptions.add(
      this.appStoreFacadeService.route.queryParams$.subscribe(params => {
        if (params && params.textNote) {
          this.inferErrorMessage(params && params.textNote);
        }
      })
    );
  }

  private initIncompatibilities() {
    this.incompatibilities$ = this.appStoreFacadeService.entity.addonsConfig$.pipe(
        filter(Boolean),
        map((addonsConfig: EntityParsed) => {
          const config = addonsConfig as unknown as AddonsConfig;
          return config.incompatibilities || {};
        })
    );

    this.subscriptions.add(
        this.incompatibilities$.subscribe(incompatibilities => {
          this.incompatibilities = incompatibilities;
        })
    );
  }

  private initSelectedAddons(selectedAddons: EntityParsed) {
    let addons = null;
    let someAddonsSelected = false;
    if (selectedAddons.value) {
      addons = (selectedAddons.value as unknown as SelectedAddons).addons;
      someAddonsSelected = addons && addons.length;
    }
    this.selectedAddons = someAddonsSelected ? new Set([...addons]) : new Set();
  }

  public getFormControl(...keys: string[]): FormControl {
    return this.formGroup.get(keys.join('.')) as FormControl;
  }

  private inferErrorMessage = textNote => {
    const textNoteParts = (textNote || '').split('|');
    if (textNoteParts.length === 1) {
      this.errorMessage = textNote;
    } else {
      this.translationKey = textNote.split('|')[0];
      this.errorMessage = textNote.split('|')[1] || '';
      // Wait for next cycle
      setTimeout(() => {
        if (this.translationKey) {
          if (this.translationKey === this.translateService.instant(this.translationKey)) {
            this.translationKey = '';
          } else {
            this.errorMessage = '';
          }
        }
      }, 0);
    }
  }

  private manageMGMCode(): void {
    this.subscriptions.add(
      combineLatest(
        [
          this.appStoreFacadeService.entity.mgmInfo$,
          this.appStoreFacadeService.entity.activationMethod$
        ]
      ).subscribe(([mgmInfo, activationMethod]) => {
        const code = mgmInfo && mgmInfo.value && mgmInfo.value.code;
        this.mgmCodeActivationMethod = (activationMethod && activationMethod.value &&
          activationMethod.value.method || 'new').toUpperCase();
        if (!code) {
          return;
        }
        this.mgmCode = code;
      })
    );
  }

  private manageCoupon(): void {
    this.subscriptions.add(
      combineLatest(
        [ this.appStoreFacadeService.entity.couponInfo$ ]
      ).subscribe(([couponInfo]) => {
        const code = couponInfo && couponInfo.value && couponInfo.value.code;
        if (!code) {
          return;
        }
        this.couponCode = code;
      })
    );
  }

  get shouldShowCouponBlock() {
    if (this.funnelHasCoupon) {
      return false;
    }
    try {
      return localStorage.getItem('enable-coupon') || this.isCouponEnabled ;
      // return window.localStorage.getItem('experimentalFeatures');
    } catch (ex) {
      return false;
    }
  }

  get shouldShowMGMBlock() {
    try {
      return localStorage.getItem('enable-mgm') || this.isInviteCodeEnabled;
      // return window.localStorage.getItem('experimentalFeatures');
    } catch (ex) {
      return false;
    }
  }

  openMgmModal() {
    combineLatest(
      [
        this.appStoreFacadeService.entity.activationMethod$
      ]
    )
      .pipe(first())
      .subscribe(([activationMethod]) => {
        const activationMethod2 = (activationMethod && activationMethod.value && activationMethod.value.method || 'new').toUpperCase();
        this.appStoreFacadeService.modals.openModal(ModalType.MgmInfo, { content: `MGM-${activationMethod2}.INSERT_CODE` });
        this.analyticsService.sendCustomPage('/popup/porta-un-amico');
    });
  }

  openCouponModal() {
    this.appStoreFacadeService.modals.openModal(ModalType.Coupon);
    this.analyticsService.sendCustomPage('/popup/inserisci-coupon');
  }

  onAddonsToggle({ addonId, toggle }) {
    const isAddonAlreadySelected = this.selectedAddons.has(addonId);
    if (!isAddonAlreadySelected && toggle) {
      this.selectedAddons.add(addonId);
    }  else if (isAddonAlreadySelected && !toggle) {
      this.selectedAddons.delete(addonId);
    }
    this.manageAddonsChange(addonId, toggle);
    this.formGroup.patchValue({
      selectedAddons: {addons: [...this.selectedAddons]}
    });
  }

  private initAddonsFormGroup() {
    this.availableAddons
      .map(addon => addon.id)
      .forEach(addonId => this.addonsFormGroup.addControl(addonId, new FormControl(this.selectedAddons.has(addonId))));
  }

  inferPayPalScript(smartTopUp: boolean) {
    if (smartTopUp) {
      const paypalConfiguration = this.environmentService.getPaypalConfiguration();
      document.getElementById('ppurl').remove();
      const headScriptNode = document.createElement('script');
      // headScriptNode.setAttribute('src', `https://www.paypal.com/sdk/js?client-id=${paypalConfiguration.clientId}&currency=EUR&locale=it_IT&intent=tokenize&vault=true`);
      headScriptNode.setAttribute('src', `https://www.paypal.com/sdk/js?client-id=${paypalConfiguration.clientId}&currency=EUR&locale=it_IT&intent=tokenize&vault=true`);
      headScriptNode.setAttribute('id', 'ppurl');
      document.head.appendChild(headScriptNode);
    } else {
      const paypalConfiguration = this.environmentService.getPaypalConfiguration();
      document.getElementById('ppurl').remove();
      const headScriptNode = document.createElement('script');
      // headScriptNode.setAttribute('src', `https://www.paypal.com/sdk/js?client-id=${paypalConfiguration.clientId}&currency=EUR&locale=it_IT&intent=tokenize&vault=true`);
      headScriptNode.setAttribute('src', `https://www.paypal.com/sdk/js?client-id=${paypalConfiguration.clientId}&currency=EUR&locale=it_IT&intent=authorize`);
      headScriptNode.setAttribute('id', 'ppurl');
      document.head.appendChild(headScriptNode);
    }
  }

  getCart() {
    this.subscriptions.add(
      combineLatest([
        this.appStoreFacadeService.entity.cart$
      ])
      .pipe(filter((args) => args.every(Boolean)))
      .subscribe(([cart]: [Cart]) => {
        this.cart = cart;
      })
    );
  }

  private manageAddonsChange(addon, toggle) {
    if (this.cart && addon) {
      let addonToCart;
      addonToCart = this.availableAddons.find(a => a.id === addon);
      const newsToCart =  {
        type: TotalPriceEnum.Addon,
        title: addonToCart.title,
        price: addonToCart.price,
        activationPrice: addonToCart.activationPrice
      };
      const newCart = this.getUpdatedCart(this.cart, newsToCart, toggle);
      if (newCart.hasChanged || newCart.newElement || newCart.toBeRemoved) {
        this.appStoreFacadeService.entity.storeEntityValue(StaticEntityKey.Cart, newCart.cart);
      }
    }

    // const operatorInfo = this.MnpOperators.find(op => op.id === operatorId);
    // if (operatorInfo && operatorInfo.configuration && operatorInfo.configuration.activationPrice) {
    //   const newCart = this.getUpdatedCart(cart, operatorInfo.configuration.activationPrice, TotalPriceEnum.Activation);
    //   if (newCart.hasChanged) {
    //     const pageName = `/popup/costo-attivazione-da-${newCart.oldPriceBeforeChange}-a-${newCart.newPriceAfterChange}`;
    //     this.analyticsService.sendCustomPage(pageName, {
    //       activation_price_new: newCart.newPriceAfterChange,
    //       activation_price_old: newCart.oldPriceBeforeChange
    //     });
    //     this.appStoreFacadeService.errors.openOperatorPriceChangeModal(
    //       {
    //         newPrice: newCart.newPriceAfterChange,
    //         oldPrice: newCart.oldPriceBeforeChange,
    //         currency: operatorInfo.configuration.activationPrice.currency
    //       },
    //       (proceed) => {
    //         if (proceed) {
    //           this.analyticsService.sendCustomEvent({
    //             custom_category: 'popup costo attivazione',
    //             custom_action: 'procedi',
    //             custom_label: pageName
    //           });
    //           this.execService.verifyOperatorUpdate(operatorId)
    //             .subscribe(updatedCart => {
    //               this.appStoreFacadeService.entity.storeEntityValue(StaticEntityKey.Cart, updatedCart.cart);
    //             });
    //         } else {
    //           this.analyticsService.sendCustomEvent({
    //             custom_category: 'popup costo attivazione',
    //             custom_action: 'chiudi',
    //             custom_label: pageName
    //           });
    //           this.formGroup.patchValue({
    //             activationMnpInfo: {
    //               operator: previousOperatorId || null
    //             }
    //           });
    //         }
    //       }
    //     );
    //   }
    // }
  }

  private getUpdatedCart(cart: Cart, selectedPrice: TotalPrice, toggle: boolean) {
    let oldPriceBeforeChange = 0;
    let newPriceAfterChange = 0;
    let hasChanged = false;
    let newElement = false;
    let toBeRemoved = false;
    const newTotalPrices = [...cart.totalPrices];
    const addonIndex = cart.totalPrices.findIndex(e => e.type === selectedPrice.type && e.title === selectedPrice.title);
    const alreadyPresent = addonIndex > -1;
    if (alreadyPresent && toggle) {
      hasChanged = true;
    } else if (alreadyPresent && !toggle) {
      toBeRemoved = true;
      newTotalPrices.splice(addonIndex, 1);
      oldPriceBeforeChange = this.getPrice(selectedPrice);
    } else if (!alreadyPresent && toggle) {
      newElement = true;
      newPriceAfterChange = this.getPrice(selectedPrice);
      newTotalPrices.push({...selectedPrice, type: TotalPriceEnum.Addon});
    }

    return {
      newElement,
      hasChanged,
      toBeRemoved,
      oldPriceBeforeChange,
      newPriceAfterChange,
      cart: {
        ...cart,
        totalPrices: newTotalPrices,
        totalPrice: {
          ...cart.totalPrice,
          price: {
            ...cart.totalPrice.price,
            value: (cart.totalPrice.price.value || 0) - oldPriceBeforeChange + newPriceAfterChange
          }
        }
      }
    };
  }

  private getPrice(selectedPrice: TotalPrice) {
    return (selectedPrice.price.value || 0) +
      (selectedPrice.type === TotalPriceEnum.Addon ? ((selectedPrice.activationPrice && selectedPrice.activationPrice.value) || 0) : 0);
  }

  private findIndexOfTrueValue(arr: boolean[]): number | null {
    for (let i = 0; i < arr.length; i++) {
      if (arr[i] === true) {
        return i;
      }
    }
    return null;
  }

  goToNextStep() {
    const smartTopUp = this.formGroup.get(this.OFFER_EXTRAS).get(this.SMART_TOP_UP).value;
    let message = '';
    if (this.hasDoublePO) {
      message = this.translateService.instant(`ENTITY.OFFER-EXTRAS.SMART_TOP_UP.POP_UP_MESSAGE_DOUBLEPO`, {deltagb: this.deltaGiga});
    } else {
      message = this.translateService.instant(`ENTITY.OFFER-EXTRAS.SMART_TOP_UP.POP_UP_MESSAGE`);
    }
    if (this.isSmartTopUpEnabled && !smartTopUp) {
      this.appStoreFacadeService.modals.openModal(
        ModalType.Confirmation,
        {
          closeButton: true,
          title: this.translateService.instant(`ENTITY.OFFER-EXTRAS.SMART_TOP_UP.POP_UP_TITLE`),
          message,
          buttonsPosition: ModalButtonPositionEnum.VERTICAL_REVERSE,
          buttonContainerClass: 'wide',
          additionalClasses: 'extra-padding wide offerta-modal-mobile',
          options: {
            confirmTranslationKey: 'ENTITY.OFFER-EXTRAS.SMART_TOP_UP.POP_UP_CONFIRM',
            cancelTranslationKey: 'ENTITY.OFFER-EXTRAS.SMART_TOP_UP.POP_UP_CANCEL',
          },
          resolve: (confirm) => {
            if (confirm) {
              this.formGroup.get(this.OFFER_EXTRAS).get(this.SMART_TOP_UP).patchValue(true);
              this.dataService.changeValue(true);
            }
            this.paypalService.switchPaypalUrlScript(smartTopUp, this.environmentService.getPaypalConfiguration());
            this.analyticsService.sendCustomEvent({
              custom_category: 'ecommerce',
              custom_action: 'ricarica automatica',
              custom_label: confirm ? 'attiva' : 'disattiva'
            });
            return super.goToNextStep();
          }
        }
      );
      this.analyticsService.sendCustomPage('/popup/attivare-ricarica-automatica');
      return;
    }
    this.paypalService.switchPaypalUrlScript(smartTopUp, this.environmentService.getPaypalConfiguration());
    return super.goToNextStep();
  }

  private sortByFullSpeed(addons: AvailableAddons) {
    const fullSpeedAddons = addons.addons.filter(addon => addon.id.includes('fullspeed'));
    const otherAddons = addons.addons.filter(addon => !addon.id.includes('fullspeed'));
    return [...fullSpeedAddons, ...otherAddons];
  }

  private loadOrderConfig(addons: OfferAddons[]): Observable<OfferAddons[]> {
    if (!this.ENABLE_ADDON_ORDERING) {
      return of(addons);
    }

    if (!this.orderConfig$) {
      this.orderConfig$ = this.http.get<AddonsOrderConfig>(
          `${this.environmentService.getAdobeDamUrl()}/01-assets/ecommerce/addons_order.json`)
        .pipe(
          shareReplay(1),
          catchError(error => {
            console.error('Error while loading order config', error);
            return of({});
          })
        );
    }

    return this.orderConfig$.pipe(
      map(order => this.sortAddons(addons, order))
    );
  }

  private sortAddons(addons: OfferAddons[], order: AddonsOrderConfig): OfferAddons[] {
    return [...addons].sort((a, b) => {
      const orderA = order[a.id] || Number.MAX_SAFE_INTEGER;
      const orderB = order[b.id] || Number.MAX_SAFE_INTEGER;
      return orderA - orderB;
    });
  }

  public getDeltaGiga() {
    return this.offerAlternative.gb - this.offer.gb;
  }

  public getAdobeDamUrl(): string {
    return this.environmentService.getAdobeDamUrl();
  }
}
